import { useMutation, useQueryClient } from '@tanstack/react-query';

import { mispickApi } from 'api/warehouse/mispick';
import { ApiError } from 'shared/errors/ApiError';

import { unpickedMispickItemsQueryKeys } from './useUnpickedMispickItemsQuery';

/**
 * Logs picks and/or out-of-stock scans for mispick remediation.
 *
 * @param {Omit<
 *   import('@tanstack/react-query').UseMutationOptions<
 *     Object,
 *     ApiError,
 *     {
 *       packLine: number,
 *       facilityID: number,
 *       shipmentId: number,
 *       pickerId?: number,
 *       picks: Array<{ sellableItemId: number, qtyPicked: number }>,
 *       outOfStock: number[],
 *     }
 *   >,
 *   'mutationFn'
 * >} [mutationOptions={}]
 */
export function useLogPicksMutation(mutationOptions = {}) {
    const queryClient = useQueryClient();
    const { onSuccess, ...rest } = mutationOptions;

    return useMutation({
        mutationFn: async (payload) => {
            const response = await mispickApi.logPicks(payload);
            if (!response?.status) {
                throw new ApiError(response?.msg ?? 'Failed to log picks', response?.statusCode, response?.data);
            }
            return response.data;
        },
        onSuccess: (data, variables, ...args) => {
            queryClient.invalidateQueries({
                queryKey: unpickedMispickItemsQueryKeys.byShipmentId(variables.shipmentId, variables.facilityID),
            });
            return onSuccess?.(data, variables, ...args);
        },
        ...rest,
    });
}
